import React, { useState, useEffect } from "react";
import { Loader2, Plus, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "Freight", "Other"];

export default function CreateTicketModal({ open, onClose, onCreated }) {
  const { user: currentUser } = useAuth();
  const isClientUser = currentUser?.role === "client_user";
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingMode, setPendingMode] = useState("user");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [v, setV] = useState({
    ticket_type: "", serial_number: "", asset_id: "", product_type: "",
    site_id: "", site_phone: "", ticket_info: "", email_cc: [""],
    from_street: "", from_city: "", from_state: "", from_zip: "", from_country: "USA",
    to_warehouse_id: "", to_street: "", to_city: "", to_state: "", to_zip: "", to_country: "USA",
    pending_user_id: "", pending_name: "",
    client_id: "",
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [m, c, w, u] = await Promise.all([
          api.get("/tickets/meta"),
          api.get("/clients"),
          api.get("/warehouses"),
          api.get("/users/assignable").catch(() => ({ data: [] })),
        ]);
        setMeta(m.data);
        const activeClients = (c.data || []).filter((x) => !x.is_archived);
        setClients(activeClients);
        setWarehouses(w.data || []);
        setUsers(u.data || []);
        setV((p) => ({
          ...p,
          ticket_type: m.data.types[0] || "",
          product_type: m.data.product_types[0] || "",
          client_id: activeClients[0]?.id || "",
          to_warehouse_id: w.data?.[0]?.id || "",
        }));
      } catch (e) { setError(apiError(e)); }
    })();
  }, [open]);

  function set(k, val) { setV((p) => ({ ...p, [k]: val })); }
  function setCc(i, val) { setV((p) => ({ ...p, email_cc: p.email_cc.map((e, idx) => (idx === i ? val : e)) })); }
  function addCc() { setV((p) => ({ ...p, email_cc: [...p.email_cc, ""] })); }
  function removeCc(i) { setV((p) => ({ ...p, email_cc: p.email_cc.filter((_, idx) => idx !== i) })); }

  function pickWarehouse(wid) {
    const w = warehouses.find((x) => x.id === wid);
    setV((p) => ({
      ...p,
      to_warehouse_id: wid,
      to_street: w ? w.street || "" : "",
      to_city: w ? w.city || "" : "",
      to_state: w ? w.state || "" : "",
      to_zip: w ? w.zip || "" : "",
      to_country: w ? w.country || "USA" : "USA",
    }));
  }

  // When Product Type = DSK + Ticket Type = New Installation, auto-fill From with the warehouse
  useEffect(() => {
    if (!open) return;
    if (v.product_type !== "DSK" || v.ticket_type !== "New Installation") return;
    if (warehouses.length === 0) return;
    const wh = warehouses.find((w) => /dolphin/i.test(w.name)) || warehouses[0];
    setV((p) => ({
      ...p,
      from_street: wh.street || "",
      from_city: wh.city || "",
      from_state: wh.state || "",
      from_zip: wh.zip || "",
      from_country: wh.country || "USA",
    }));
  }, [v.product_type, v.ticket_type, warehouses.length, open]);

  async function submit(e) {
    e.preventDefault();
    setError(null); setSaving(true);
    try {
      const payload = {
        ticket_type: v.ticket_type, serial_number: v.serial_number, asset_id: v.asset_id,
        product_type: v.product_type, site_id: v.site_id, site_phone: v.site_phone,
        ticket_info: v.ticket_info, email_cc: v.email_cc.filter(Boolean), client_id: v.client_id,
        from_address: { street: v.from_street, city: v.from_city, state: v.from_state, zip: v.from_zip, country: v.from_country },
        to_address: {
          warehouse_id: v.to_warehouse_id || null,
          street: v.to_street, city: v.to_city, state: v.to_state, zip: v.to_zip, country: v.to_country,
        },
        pending_with: pendingMode === "user" ? { user_id: v.pending_user_id || null } : { name: v.pending_name || null },
      };
      const { data } = await api.post("/tickets", payload);
      onCreated && onCreated(data);
      onClose();
    } catch (err) { setError(apiError(err)); } finally { setSaving(false); }
  }

  if (!open) return null;
  if (!meta) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <div className="mx-auto my-8 max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-bold tracking-tight">Create New Ticket</h2>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-6 p-6">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2"><Label>Ticket Type *</Label>
              <select className={selectClass} value={v.ticket_type} onChange={(e) => set("ticket_type", e.target.value)} required>
                {meta.types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-2"><Label>Serial Number (Optional)</Label><Input value={v.serial_number} onChange={(e) => set("serial_number", e.target.value)} placeholder="e.g., SN12345" /></div>
            <div className="space-y-2"><Label>Asset ID (Optional)</Label><Input value={v.asset_id} onChange={(e) => set("asset_id", e.target.value)} placeholder="e.g., ASSET-12345" /></div>
            <div className="space-y-2"><Label>Product Type</Label>
              <select className={selectClass} value={v.product_type} onChange={(e) => set("product_type", e.target.value)}>
                <option value="">Select Type</option>
                {meta.product_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-2"><Label>Site ID</Label><Input value={v.site_id} onChange={(e) => set("site_id", e.target.value)} placeholder="e.g., 12345" /></div>
            <div className="space-y-2"><Label>Site Phone Number</Label><Input value={v.site_phone} onChange={(e) => set("site_phone", e.target.value)} placeholder="+1 555-123-4567" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Organization</Label>
              {isClientUser ? (
                <Input
                  value={clients[0]?.name || currentUser?.client_name || "Your organization"}
                  disabled
                  data-testid="client-locked"
                />
              ) : (
                <select className={selectClass} value={v.client_id} onChange={(e) => set("client_id", e.target.value)}>
                  <option value="">No client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Ticket Info</Label><Input value={v.ticket_info} onChange={(e) => set("ticket_info", e.target.value)} placeholder="e.g., Screen issues after rain" /></div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Email CC (Optional - emails for ticket updates)</Label>
              {v.email_cc.map((e, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={e} onChange={(ev) => setCc(i, ev.target.value)} placeholder="e.g., manager@store.com" />
                  {v.email_cc.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => removeCc(i)}><Trash2 className="h-4 w-4" /></Button>}
                  {i === v.email_cc.length - 1 && <Button type="button" variant="outline" size="icon" onClick={addCc}><Plus className="h-4 w-4" /></Button>}
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2"><Label className="text-sm font-semibold">From Address</Label>
              {v.product_type === "DSK" && v.ticket_type === "New Installation" && (
                <p className="text-xs text-sky-700" data-testid="from-warehouse-hint">Auto-filled with the Dolphin warehouse for new DSK installations. You can still edit below.</p>
              )}
              <Input placeholder="Street" value={v.from_street} onChange={(e) => set("from_street", e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="City" value={v.from_city} onChange={(e) => set("from_city", e.target.value)} />
                <Input placeholder="State" value={v.from_state} onChange={(e) => set("from_state", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="ZIP Code" value={v.from_zip} onChange={(e) => set("from_zip", e.target.value)} />
                <Input placeholder="Country" value={v.from_country} onChange={(e) => set("from_country", e.target.value)} />
              </div>
            </div>
            <div className="space-y-2"><Label className="text-sm font-semibold">To Address</Label>
              <select className={selectClass} value={v.to_warehouse_id} onChange={(e) => pickWarehouse(e.target.value)}>
                <option value="">Custom Address</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <Input placeholder="Street" value={v.to_street} onChange={(e) => set("to_street", e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="City" value={v.to_city} onChange={(e) => set("to_city", e.target.value)} />
                <Input placeholder="State" value={v.to_state} onChange={(e) => set("to_state", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="ZIP Code" value={v.to_zip} onChange={(e) => set("to_zip", e.target.value)} />
                <Input placeholder="Country" value={v.to_country} onChange={(e) => set("to_country", e.target.value)} />
              </div>
              {v.to_warehouse_id && <p className="text-xs text-slate-500">Auto-filled from warehouse. Edit fields above to override.</p>}
            </div>
          </div>
          {!isClientUser && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Pending With</Label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="radio" checked={pendingMode === "user"} onChange={() => setPendingMode("user")} /> System user</label>
              <label className="flex items-center gap-2"><input type="radio" checked={pendingMode === "name"} onChange={() => setPendingMode("name")} /> External contact (free text)</label>
            </div>
            {pendingMode === "user" ? (
              <select className={selectClass} value={v.pending_user_id} onChange={(e) => set("pending_user_id", e.target.value)}>
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>)}
              </select>
            ) : (
              <Input value={v.pending_name} onChange={(e) => set("pending_name", e.target.value)} placeholder="e.g., T-Mobile store contact" />
            )}
          </div>
          )}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Ticket</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
